export type Row = {
  key: string;
  kind: string;
  /** The raw principal name — `i-0abc…` for an instance. Identity, not display. */
  name: string;
  /** What to SHOW: `nordea-cluster-node (i-0abc…)` where the API could name it. */
  label: string;
  usd: number;
  /** undefined = no override, use the default; number = the effective cap, from
   *  whichever tier resolved it (day override, standing user/group grant, or the
   *  plain default — see `limitSource`); null = uncapped. */
  limitUsd: number | null | undefined;
  /**
   * WHERE `limitUsd` came from: `"day"` is an operator's override for today only;
   * `"user"` / `"group:<name>"` are standing grants that persist past midnight;
   * `"default"` is the plain global cap. Since the day API started resolving
   * standing limits, `limitUsd` is a number for every row — including one sitting
   * on the plain default — so `limitUsd !== undefined` can no longer stand in for
   * "this row has something worth calling out". `limitSource` is what actually
   * distinguishes them.
   *
   * Optional only because `Row` is shared: every caller that builds one from an API
   * response that resolves standing limits (the day route, and now the principal
   * drill-down too — see `renderPrincipal` in main.ts) always sets it.
   */
  limitSource?: string;
  suspended: boolean;
  /** A configured group's membership snapshot was missing when this row's cap was
   *  resolved, so `limitUsd` is a LOWER BOUND on the true cap, not necessarily the
   *  true value — see `resolveLimit` in `src/limits.ts`. `statusOf` uses this to
   *  avoid asserting "Over limit" on a guess the meter itself declined to make. */
  degraded?: "missing-snapshot";
};

export function effectiveLimit(r: Row, defaultThresholdUsd: number): number | null {
  return r.limitUsd === undefined ? defaultThresholdUsd : r.limitUsd;
}

export function usagePct(r: Row, defaultThresholdUsd: number): number {
  const limit = effectiveLimit(r, defaultThresholdUsd);
  return limit === null ? 0 : (r.usd / limit) * 100;
}

/** `"group:BD"` -> `"BD"`. */
const groupName = (source: string): string => source.slice("group:".length);

export function statusOf(r: Row, defaultThresholdUsd: number): { label: string; danger: boolean } {
  // A real, current enforcement action — not a guess this function is making — so
  // it outranks the degraded check below regardless of why the principal was
  // suspended (their real cap, or the fallback ceiling while degraded).
  if (r.suspended) return { label: "Suspended", danger: true };
  const limit = effectiveLimit(r, defaultThresholdUsd);
  if (limit !== null && r.usd >= limit) {
    // `limitUsd` is a LOWER bound here, not the true cap, and the principal is NOT
    // suspended (checked above) — the meter itself declined to enforce on this same
    // guess. Asserting "Over limit" would tell an operator to do by hand exactly
    // what the automation correctly refused to do automatically.
    if (r.degraded) return { label: "Cap unknown (group snapshot missing)", danger: false };
    return { label: "Over limit", danger: true };
  }
  if (r.limitSource === "day") return { label: "Limit raised", danger: false };
  if (r.limitSource === "user") return { label: "Standing limit", danger: false };
  if (r.limitSource?.startsWith("group:")) {
    return { label: `Standing limit (${groupName(r.limitSource)})`, danger: false };
  }
  return { label: "Under limit", danger: false };
}

/**
 * The extra context shown beside a principal's cap on its drill-down tile. Same
 * distinction as `statusOf`, applied to a plain caption rather than a badge: a
 * day-only override resets tonight, a standing grant does not, and one
 * "(override today)" suffix for both would tell a BD member their $200 group
 * grant is about to disappear at midnight.
 */
export function capSourceNote(source: string | undefined): string {
  if (source === "day") return " (override today)";
  if (source === "user") return " (standing limit)";
  if (source?.startsWith("group:")) return ` (standing limit: ${groupName(source)})`;
  return "";
}

export const money = (v: number): string =>
  "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
